// Reconstructing what a group was working on during one session.
//
// A session snapshots the standards its group carried at the moment it started
// (`standard_prefix`, `lesson_standard_prefixes`), but not the lesson, the
// track, or the bundle those standards came from. Those live on the classroom's
// current assignment, which the teacher edits freely: reassigning a group,
// swapping a bundle's lesson, or deleting a bundle outright — all ordinary
// weekly actions.
//
// So the current assignment is only usable as this session's context when it
// still matches what the session froze. The standards are the check: if today's
// bundle addresses the same set the session recorded, it is the same
// assignment, and its lesson and track can be trusted. If they differ, the
// group has moved on and the session keeps its own snapshot rather than
// borrowing a title from work it never did.
//
// Matching on content, not on `last_updated_at` — that field only tracks the
// bundle row's own edits, and a per-group track change does not bump it.

const sameCodes = (a = [], b = []) => {
  if (a.length !== b.length || a.length === 0) return false;
  const left = [...a].sort();
  const right = [...b].sort();
  return left.every((code, i) => code === right[i]);
};

const prefixesOf = (session) =>
  (session?.lesson_standard_prefixes || [])
    .map((p) => p?.standard_prefix || p)
    .filter(Boolean);

// The components a group is filtered down to, from either side: the session
// snapshots ids, the live assignment carries whole rows.
const lcIdsOf = (source) =>
  (source?.learning_component_ids || source?.learning_components || [])
    .map((lc) => (typeof lc === "string" ? lc : lc?.lc_id))
    .filter(Boolean);

// Whether the group is still filtered the way it was when the session ran.
//
// Standards alone cannot answer this: refiltering a group from one learning
// component to another of the SAME standard leaves standard_code untouched, so
// the session would borrow a title naming work it never did.
const sameFilter = (session, assignment) => {
  const before = lcIdsOf(session);
  const now = lcIdsOf(assignment);
  if (before.length === 0 && now.length === 0) return true;
  return sameCodes(before, now);
};

/**
 * Find the group's current assignment row and its bundle, if it is still the
 * one this session ran on.
 *
 * @param {object} session A session-history row: group_code, standard_prefix,
 *   lesson_standard_prefixes.
 * @param {Array<object>} bundles `ClassroomLessonsOut.lessons` for the
 *   session's classroom.
 * @returns {{assignment: object, bundle: object}|null} Null when the group is
 *   no longer on the assignment it ran, or has none at all.
 */
export const assignmentForSession = (session, bundles = []) => {
  const groupCode = session?.group_code;
  if (!groupCode || !Array.isArray(bundles)) return null;

  const sessionPrefixes = prefixesOf(session);

  for (const bundle of bundles) {
    const assignment = (bundle?.assignments || []).find(
      (a) => a?.group_code === groupCode,
    );
    if (!assignment) continue;

    // The group has to be on the same standards AND the same filter — either
    // one having moved means this is no longer the assignment that ran.
    if (!sameFilter(session, assignment)) return null;

    // A core-track lesson group froze the whole addressing set; it has to still
    // be the same set.
    if (sessionPrefixes.length > 0) {
      return sameCodes(sessionPrefixes, assignment.standard_codes || [])
        ? { assignment, bundle }
        : null;
    }

    // Every other flow froze a single prefix.
    if (session.standard_prefix && assignment.standard_code === session.standard_prefix) {
      return { assignment, bundle };
    }

    return null;
  }

  return null;
};

/**
 * The assignment row for a session, in the shape the group-card title helper
 * reads, falling back to the session's own snapshot when the live assignment
 * can no longer be trusted.
 *
 * The fallback carries standards only — no track, no lesson, and no learning
 * components — which is exactly what the session knows for certain. The
 * snapshot holds component ids but not their names, so there is nothing to
 * title with; `resolveGroupCardTitle` degrades to titling by standard, which is
 * the honest answer for a group that has since been refiltered.
 *
 * @param {object} session A session-history row.
 * @param {Array<object>} bundles Current bundles for the classroom.
 * @returns {{assignment: object, bundle: object}}
 */
export const sessionCardInput = (session, bundles = []) => {
  const matched = assignmentForSession(session, bundles);
  if (matched) return matched;

  return {
    assignment: {
      group_code: session?.group_code,
      standard_code: session?.standard_prefix || null,
      standard_codes: prefixesOf(session).length ? prefixesOf(session) : null,
      standard_name: session?.standard_prefix_name || null,
      question_filter: null,
      learning_components: [],
    },
    bundle: {},
  };
};
